// BubbleChart.jsx — Dashboard component
// X axis = P&L %  |  Y axis = Allocation %  |  Size = Invested $  |  Color = profit/loss
/* eslint-disable */
import { useState, useRef, useEffect } from "react";
import { C, fmt$, fmtPct } from "../shared/globals.js";

export default function BubbleChart({ assetData = [], totalValue = 0 }) {
  const wrapRef = useRef(null);
  const [w, setW] = useState(600);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    const obs = new ResizeObserver(es => {
      const width = es[0].contentRect.width;
      if (width > 0) setW(width);
    });
    if (wrapRef.current) obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  if (!assetData.length) return null;

  const H   = 320;
  const PAD = { top: 24, right: 24, bottom: 40, left: 52 };
  const CW  = w - PAD.left - PAD.right;
  const CH  = H - PAD.top - PAD.bottom;

  const maxInvested = Math.max(...assetData.map(a => a.totalDollars), 1);
  const pnlValues   = assetData.map(a => a.pnlPct);
  const rawXMin     = Math.min(...pnlValues);
  const rawXMax     = Math.max(...pnlValues);
  const xPad        = Math.max(Math.abs(rawXMax - rawXMin) * 0.25, 5);
  const xMin        = rawXMin - xPad;
  const xMax        = rawXMax + xPad;
  const xRange      = xMax - xMin || 20;

  const allocValues = assetData.map(a => totalValue > 0 ? (a.currentValue / totalValue) * 100 : 0);
  const yMax        = Math.max(...allocValues) * 1.3 || 10;

  const toX = v => PAD.left + ((v - xMin) / xRange) * CW;
  const toY = v => PAD.top + (1 - v / yMax) * CH;
  const toR = v => 7 + (v / maxInvested) * 28;

  // Max 6 ticks, evenly distributed
  const MAX_TICKS = 6;
  const rawStep = xRange / MAX_TICKS;
  const mag  = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const step = [1,2,2.5,5,10].map(s => s*mag).find(s => s >= rawStep) || mag*10;
  const xStart = Math.ceil(xMin / step) * step;
  const xTicks = [];
  for (let t = xStart; t <= xMax; t += step) xTicks.push(t);

  const yTicks = [0, yMax * 0.25, yMax * 0.5, yMax * 0.75, yMax].map(v => Math.round(v));

  return (
    <div ref={wrapRef} style={{ width: "100%", position: "relative" }}>
      <svg width={w} height={H} style={{ overflow: "visible", display: "block" }}>

        {/* Grid */}
        {yTicks.map(t => (
          <line key={t} x1={PAD.left} y1={toY(t)} x2={PAD.left + CW} y2={toY(t)}
            stroke="var(--border)" strokeWidth={1} strokeDasharray="3 3" />
        ))}

        {/* Zero X line */}
        {xMin < 0 && xMax > 0 && (
          <line x1={toX(0)} y1={PAD.top} x2={toX(0)} y2={PAD.top + CH}
            stroke="var(--border2)" strokeWidth={1.5} />
        )}

        {/* Y labels */}
        {yTicks.map(t => (
          <text key={t} x={PAD.left - 6} y={toY(t) + 4}
            textAnchor="end" fontSize={9} fontFamily="Montserrat,sans-serif" fill="var(--text4)">
            {t}%
          </text>
        ))}

        {/* X labels */}
        {xTicks.map(t => (
          <text key={t} x={toX(t)} y={PAD.top + CH + 16}
            textAnchor="middle" fontSize={9} fontFamily="Montserrat,sans-serif" fill="var(--text4)">
            {t > 0 ? `+${t}` : t}%
          </text>
        ))}

        {/* Axis titles */}
        <text x={PAD.left + CW / 2} y={H - 2}
          textAnchor="middle" fontSize={8} fontFamily="Montserrat,sans-serif"
          fill="var(--text4)" letterSpacing="0.15em">
          P&amp;L %
        </text>
        <text x={10} y={PAD.top + CH / 2}
          textAnchor="middle" fontSize={8} fontFamily="Montserrat,sans-serif"
          fill="var(--text4)" letterSpacing="0.15em"
          transform={`rotate(-90, 10, ${PAD.top + CH / 2})`}>
          ALLOCATION %
        </text>

        {/* Bubbles */}
        {assetData.map(a => {
          const alloc  = totalValue > 0 ? (a.currentValue / totalValue) * 100 : 0;
          const cx     = toX(a.pnlPct);
          const cy     = toY(alloc);
          const r      = toR(a.totalDollars);
          const col    = a.pnl >= 0 ? C.green : C.red;
          const hov    = tooltip?.ticker === a.ticker;
          return (
            <g key={a.ticker}
              onMouseEnter={() => setTooltip({ ticker: a.ticker, pnlPct: a.pnlPct, pnl: a.pnl, alloc, invested: a.totalDollars, value: a.currentValue, cx, cy, r })}
              onMouseLeave={() => setTooltip(null)}
              style={{ cursor: "pointer" }}>
              <circle cx={cx} cy={cy} r={r}
                fill={col} fillOpacity={hov ? 0.85 : 0.45}
                stroke={col} strokeWidth={hov ? 2 : 1}
                style={{ transition: "all 0.15s" }} />
              {r > 13 && (
                <text x={cx} y={cy + 4}
                  textAnchor="middle"
                  fontSize={Math.min(10, r * 0.65)}
                  fontFamily="Montserrat,sans-serif" fontWeight={700}
                  fill="var(--text)" fillOpacity={0.9} pointerEvents="none">
                  {a.ticker.slice(0, 5)}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: "absolute",
          left: Math.min(tooltip.cx + tooltip.r + 10, w - 165),
          top: Math.max(tooltip.cy - 65, 0),
          background: "var(--bg3)", border: "1px solid var(--border2)",
          borderRadius: 8, padding: "10px 14px",
          pointerEvents: "none", zIndex: 10, minWidth: 148,
        }}>
          <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 12, fontWeight: 900, color: C.orange, marginBottom: 6 }}>
            {tooltip.ticker}
          </div>
          {[
            ["P&L %",      fmtPct(tooltip.pnlPct), tooltip.pnl >= 0 ? C.green : C.red],
            ["P&L $",      fmt$(tooltip.pnl),       tooltip.pnl >= 0 ? C.green : C.red],
            ["Invested",   fmt$(tooltip.invested),  "var(--text2)"],
            ["Value",      fmt$(tooltip.value),     "var(--text2)"],
            ["Allocation", `${tooltip.alloc.toFixed(1)}%`, "var(--text2)"],
          ].map(([label, val, color]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 2 }}>
              <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 9, color: "var(--text4)", letterSpacing: "0.1em" }}>{label}</span>
              <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700, color }}>{val}</span>
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div style={{ display: "flex", justifyContent: "center", gap: 20, marginTop: 6 }}>
        {[[C.green, "Profit"], [C.red, "Loss"]].map(([col, label]) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: col, opacity: 0.6 }} />
            <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 9, color: "var(--text4)", letterSpacing: "0.1em" }}>{label}</span>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", border: "1px solid var(--text5)" }} />
          <div style={{ width: 14, height: 14, borderRadius: "50%", border: "1px solid var(--text5)" }} />
          <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 9, color: "var(--text4)", letterSpacing: "0.1em" }}>Size = Invested $</span>
        </div>
      </div>
    </div>
  );
}
